import React, { useState, useEffect, useRef } from 'react';
import { matchVideosService } from '../../../shared/services/matchVideosService';
import { videoTagsService } from '../../../shared/services/videoTagsService';
import { VideoPlayer, VideoPlayerMethods } from '../../../shared/components/video';
import VideoTimelineOverlay, { TimelineEvent } from '../../../shared/components/video/VideoTimelineOverlay';

interface MatchVideo {
  id: string;
  match_id: string;
  video_url: string;
  thumbnail_url: string | null;
  video_order: number;
  created_at: string;
  updated_at: string;
}

interface VideoTimelineTabProps {
  matchId: string;
  isActive?: boolean;
  pendingSeek?: { videoId: string; time: number } | null;
  onSeekComplete?: () => void;
}

/**
 * Video Timeline Tab Component
 * 
 * Displays video player with timeline overlay showing events from match video tags.
 * Allows users to toggle between key events only or all events, navigate between 
 * multiple videos, and adjust timeline marker ranges.
 */
const VideoTimelineTab: React.FC<VideoTimelineTabProps> = ({ 
  matchId, 
  isActive = true,
  pendingSeek,
  onSeekComplete
}) => {
  const [videos, setVideos] = useState<MatchVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [allVideoTags, setAllVideoTags] = useState<TimelineEvent[]>([]);
  const [showKeyEventsOnly, setShowKeyEventsOnly] = useState(true);
  const videoPlayerRef = useRef<VideoPlayerMethods | null>(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [timelineRangeBefore, setTimelineRangeBefore] = useState<number>(5);
  const [timelineRangeAfter, setTimelineRangeAfter] = useState<number>(5);

  // Fetch videos when component mounts or matchId changes
  useEffect(() => {
    if (!matchId) {
      setError('No match ID provided');
      setLoading(false);
      return;
    }

    const fetchVideos = async () => {
      try {
        setLoading(true);
        const result = await matchVideosService.getMatchVideos(matchId);
        
        if (result.success && result.data) {
          setVideos(result.data);
        } else {
          setVideos([]);
        }
      } catch (err) {
        console.error('Error fetching videos:', err);
        setError('Failed to fetch videos');
        setVideos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [matchId]);

  // Fetch key events with video timing when videos are loaded
  useEffect(() => {
    if (matchId && videos.length > 0) {
      const fetchKeyEvents = async () => {
        try {
          const result = await videoTagsService.getKeyEventsWithVideoTiming(matchId);
          if (result.success && result.data) {
            setTimelineEvents(result.data);
          } else {
            setTimelineEvents([]);
          }
        } catch (err) {
          console.error('Error fetching key events:', err);
          setTimelineEvents([]);
        }
      };

      fetchKeyEvents();
    }
  }, [matchId, videos.length]);

  // Fetch all video tags when videos are loaded
  useEffect(() => {
    if (matchId && videos.length > 0) {
      const fetchAllVideoTags = async () => {
        try {
          const result = await videoTagsService.getVideoTags(matchId);
          if (result.success && result.data) {
            // Transform VideoTag[] to TimelineEvent[]
            const transformedEvents: TimelineEvent[] = result.data
              .filter(tag => tag.video_time_seconds !== null && tag.video_id !== null)
              .map(tag => ({
                eventId: tag.id,
                matchMinute: tag.match_minute,
                eventTimestamp: tag.created_at,
                statType: tag.stat_type ? {
                  id: tag.stat_type.id,
                  stat_key: tag.stat_type.stat_key,
                  display_name: tag.stat_type.display_name
                } : null,
                team: tag.team ? { id: tag.team.id, name: tag.team.name } : null,
                primaryPlayer: tag.player ? {
                  id: tag.player.id,
                  full_name: tag.player.full_name
                } : null,
                videoTimeSeconds: tag.video_time_seconds,
                videoId: tag.video_id,
                tags: Array.isArray(tag.tags) ? tag.tags : []
              }));
            setAllVideoTags(transformedEvents);
          } else {
            setAllVideoTags([]);
          }
        } catch (err) {
          console.error('Error fetching all video tags:', err);
          setAllVideoTags([]);
        }
      };

      fetchAllVideoTags();
    }
  }, [matchId, videos.length]);

  // Refresh videos when tab becomes active (in case videos were added)
  useEffect(() => {
    if (isActive && matchId) {
      const refreshVideos = async () => {
        const result = await matchVideosService.getMatchVideos(matchId);
        if (result.success && result.data) {
          setVideos(result.data);
        }
      };
      refreshVideos();
    }
  }, [isActive, matchId]);

  // Handle pending seek from Event Clips tab
  useEffect(() => {
    if (pendingSeek && videos.length > 0 && videoPlayerRef.current) {
      const videoIndex = videos.findIndex(v => v.id === pendingSeek.videoId);
      if (videoIndex !== -1) {
        // Switch to the correct video if needed
        if (selectedVideoIndex !== videoIndex) {
          setSelectedVideoIndex(videoIndex);
          // Wait for video to switch, then seek
          setTimeout(() => {
            if (videoPlayerRef.current) {
              try {
                videoPlayerRef.current.seekTo(pendingSeek.time);
                videoPlayerRef.current.play();
                // Scroll to video player
                setTimeout(() => {
                  const videoElement = document.getElementById('video-player-container');
                  if (videoElement) {
                    videoElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100);
              } catch (error) {
                console.warn('Error seeking video:', error);
              }
            }
            if (onSeekComplete) {
              onSeekComplete();
            }
          }, 1000);
        } else {
          // Same video - seek immediately
          if (videoPlayerRef.current) {
            try {
              videoPlayerRef.current.seekTo(pendingSeek.time);
              videoPlayerRef.current.play();
              // Scroll to video player
              setTimeout(() => {
                const videoElement = document.getElementById('video-player-container');
                if (videoElement) {
                  videoElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            } catch (error) {
              console.warn('Error seeking video:', error);
            }
          }
          if (onSeekComplete) {
            onSeekComplete();
          }
        }
      }
    }
  }, [pendingSeek, videos, selectedVideoIndex, onSeekComplete]);

  const handleVideoSelect = (index: number) => {
    setSelectedVideoIndex(index);
    // Reset duration and time when switching videos
    setVideoDuration(0);
    setCurrentTime(0);
  };

  // Handle timeline event click - jump to video time
  const handleTimelineEventClick = (videoTimeSeconds: number, videoId: string) => {
    // Find the video index for this videoId
    const videoIndex = videos.findIndex(v => v.id === videoId);
    if (videoIndex !== -1) {
      // Switch to the correct video if needed
      if (selectedVideoIndex !== videoIndex) {
        setSelectedVideoIndex(videoIndex);
        // Wait for video to switch, then seek
        setTimeout(() => {
          if (videoPlayerRef.current) {
            try {
              videoPlayerRef.current.seekTo(videoTimeSeconds);
              videoPlayerRef.current.play();
            } catch (error) {
              console.warn('Error seeking video:', error);
            }
          }
          // Scroll to video player
          setTimeout(() => {
            const videoElement = document.getElementById('video-player-container');
            if (videoElement) {
              videoElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }, 1000);
      } else {
        // Same video - seek immediately
        if (videoPlayerRef.current) {
          try {
            videoPlayerRef.current.seekTo(videoTimeSeconds);
            videoPlayerRef.current.play();
          } catch (error) {
            console.warn('Error seeking video:', error);
          }
        }
        // Scroll to video player
        setTimeout(() => {
          const videoElement = document.getElementById('video-player-container');
          if (videoElement) {
            videoElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="w-32 h-32 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
            <svg className="w-16 h-16 text-gray-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Videos</h2>
          <p className="text-gray-500">Please wait...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Videos</h2>
          <p className="text-gray-500 mb-4">{error}</p>
        </div>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="w-32 h-32 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Videos Available</h2>
          <p className="text-gray-500 mb-4">No videos found for this match</p>
          <div className="text-sm text-gray-400">
            Match ID: {matchId}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Video Player */}
      <div id="video-player-container" className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <div className="aspect-video bg-black relative group overflow-visible">
          {videos[selectedVideoIndex] ? (
            <>
              <VideoPlayer
                ref={videoPlayerRef}
                key={`video-${videos[selectedVideoIndex].id}-${videos[selectedVideoIndex].video_url}`}
                videoUrl={videos[selectedVideoIndex].video_url}
                className="w-full h-full"
                controls={true}
                onReady={() => {
                  // Get video duration when ready
                  const updateDuration = () => {
                    if (videoPlayerRef.current) {
                      try {
                        const duration = videoPlayerRef.current.getDuration();
                        if (duration > 0) {
                          setVideoDuration(duration);
                        }
                      } catch (error) {
                        console.warn('Error getting video duration:', error);
                      }
                    }
                  };
                  
                  // Try immediately
                  updateDuration();
                  
                  // Also try after a short delay (YouTube API sometimes needs time)
                  setTimeout(updateDuration, 500);
                }}
                onTimeUpdate={(time) => {
                  setCurrentTime(time);
                  // Update duration if we don't have it yet
                  if (videoDuration === 0 && videoPlayerRef.current) {
                    try {
                      const duration = videoPlayerRef.current.getDuration();
                      if (duration > 0) {
                        setVideoDuration(duration);
                      }
                    } catch (error) {
                      // Ignore errors
                    }
                  }
                }}
              />
              
              {/* Timeline Overlay */}
              <VideoTimelineOverlay
                events={showKeyEventsOnly ? timelineEvents : allVideoTags}
                videoDuration={videoDuration}
                currentVideoId={videos[selectedVideoIndex].id}
                onEventClick={handleTimelineEventClick}
                className="z-20"
                rangeBefore={timelineRangeBefore}
                rangeAfter={timelineRangeAfter}
                currentTime={currentTime}
              />
            </>
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          
          {/* Video number */}
          <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded z-30">
            {selectedVideoIndex + 1} of {videos.length}
          </div>
        </div>
      </div>

      {/* Controls Strip */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Event Filter */}
          <div className="flex items-center space-x-3">
            <span className="text-xs font-medium text-gray-700 whitespace-nowrap">Event Filter:</span>
            <div className="flex items-center space-x-1.5">
              <button
                onClick={() => setShowKeyEventsOnly(true)}
                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                  showKeyEventsOnly
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Key Events
              </button>
              <button
                onClick={() => setShowKeyEventsOnly(false)}
                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                  !showKeyEventsOnly
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All Events
              </button>
            </div>
            <span className="text-xs text-gray-500">
              ({videos[selectedVideoIndex] ? (
                showKeyEventsOnly 
                  ? timelineEvents.filter(e => e.videoId === videos[selectedVideoIndex].id && e.videoTimeSeconds !== null).length
                  : allVideoTags.filter(e => e.videoId === videos[selectedVideoIndex].id && e.videoTimeSeconds !== null).length
              ) : 0} markers)
            </span>
          </div>

          {/* Divider */}
          <div className="h-6 w-px bg-gray-300"></div>

          {/* Marker Range */}
          <div className="flex items-center space-x-2">
            <span className="text-xs font-medium text-gray-700 whitespace-nowrap">Range:</span>
            <div className="flex items-center space-x-1.5">
              <input
                type="number"
                min="0"
                max="60"
                value={timelineRangeBefore}
                onChange={(e) => setTimelineRangeBefore(Math.max(0, Math.min(60, parseInt(e.target.value) || 0)))}
                className="w-12 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-500">s before</span>
              <span className="text-xs text-gray-400">/</span>
              <input
                type="number"
                min="0"
                max="60"
                value={timelineRangeAfter}
                onChange={(e) => setTimelineRangeAfter(Math.max(0, Math.min(60, parseInt(e.target.value) || 0)))}
                className="w-12 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-500">s after</span>
            </div>
          </div>
        </div>
      </div>

      {/* Video Thumbnails */}
      {videos.length > 1 && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Other Videos</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {videos.map((video, index) => (
              <button
                key={video.id}
                onClick={() => handleVideoSelect(index)}
                className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                  selectedVideoIndex === index 
                    ? 'border-blue-500 ring-2 ring-blue-200' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {video.thumbnail_url ? (
                  <img
                    src={video.thumbnail_url}
                    alt={`Video ${index + 1} thumbnail`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                
                {/* Video number */}
                <div className="absolute top-1 left-1 bg-black bg-opacity-70 text-white text-xs px-1.5 py-0.5 rounded text-[10px]">
                  {index + 1}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoTimelineTab;

